import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  QrCode,
  Share2,
  TrendingUp,
  Users,
  Download,
  Search,
  Mail,
  Phone,
  User,
  Building,
  Calendar,
  MessageSquare,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogClose
} from '../../../components/ui/Dialog';
import { getMyProfileAnalytics, type ProfileContactView } from '../../../api/PublicProfile/publicProfileApi';

interface InteractionsTabProps {
  selectedPersona: string;
  contacts?: ProfileContactView[];
  profileId?: number;
}

interface AnalyticsData {
  totalViews: number;
  qrScans: number;
  profileShares: number;
  viewsTrend: string;
  scansTrend: string;
  sharesTrend: string;
}

const InteractionsTab: React.FC<InteractionsTabProps> = ({ selectedPersona, contacts: propContacts, profileId }) => {
  const { t } = useTranslation();

  // Analytics data from API
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    totalViews: 0,
    qrScans: 0,
    profileShares: 0,
    viewsTrend: '+0%',
    scansTrend: '+0%',
    sharesTrend: '+0%'
  });
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);

  // Fetch analytics on mount
  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoadingAnalytics(true);
      try {
        const result = await getMyProfileAnalytics();
        if (result.status === 200 && result.payload) {
          // If profileId is specified, find that specific profile's analytics
          // Otherwise, aggregate all profiles
          let analytics = result.payload;
          if (profileId) {
            analytics = result.payload.filter(a => a.profileId === profileId);
          }

          // Aggregate the analytics
          const totalViews = analytics.reduce((sum, a) => sum + a.totalViews, 0);
          const totalShares = analytics.reduce((sum, a) => sum + a.totalShares, 0);
          const totalQRScans = analytics.reduce((sum, a) => sum + a.totalQRScans, 0);

          setAnalyticsData({
            totalViews,
            qrScans: totalQRScans,
            profileShares: totalShares,
            viewsTrend: '+0%', // Trend calculation would require historical data
            scansTrend: '+0%',
            sharesTrend: '+0%'
          });
        }
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setIsLoadingAnalytics(false);
      }
    };

    fetchAnalytics();
  }, [profileId]);

  // Use prop contacts or empty array
  const contactDetails = propContacts || [];

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedContact, setSelectedContact] = useState<ProfileContactView | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  // Filter and sort contacts
  const filteredContacts = useMemo(() => {
    return contactDetails
      .filter(contact => {
        const query = searchQuery?.toLowerCase();
        if (!query) return true;
        return (
          contact?.fullName?.toLowerCase()?.includes(query) ||
          contact?.email?.toLowerCase()?.includes(query) ||
          contact?.company?.toLowerCase()?.includes(query) ||
          contact?.phone?.includes(query)
        );
      })
      .sort((a, b) => {
        if (sortBy === 'newest') {
          return new Date(b?.submittedAt).getTime() - new Date(a?.submittedAt).getTime();
        } else if (sortBy === 'oldest') {
          return new Date(a?.submittedAt).getTime() - new Date(b?.submittedAt).getTime();
        } else if (sortBy === 'name') {
          return a?.fullName?.localeCompare(b?.fullName);
        }
        return 0;
      });
  }, [contactDetails, searchQuery, sortBy]);

  const handleViewDetails = (contact: ProfileContactView) => {
    setSelectedContact(contact);
    setIsDetailsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedContact(null);
  };

  const handleSaveToPhone = (contact: ProfileContactView) => {
    // Generate vCard data
    const nameParts = contact?.fullName?.split(' ') || [];
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${contact?.fullName}
N:${nameParts[1] || ''};${nameParts[0] || ''};;;
EMAIL:${contact?.email}
TEL:${contact?.phone || ''}
ORG:${contact?.company || ''}
NOTE:${contact?.message || 'Contact from profile interaction'}
END:VCARD`;

    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${contact?.fullName?.replace(/\s+/g, '_')}.vcf`;
    link?.click();
    window.URL?.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    // Export all contacts as CSV
    const csvHeader = 'Full Name,Email,Phone,Company,Message,Submitted At\n';
    const csvRows = contactDetails?.map(contact =>
      `"${contact?.fullName}","${contact?.email}","${contact?.phone || ''}","${contact?.company || ''}","${contact?.message || ''}","${new Date(contact?.submittedAt).toLocaleString()}"`
    )?.join('\n');

    const csvContent = csvHeader + csvRows;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `profile_interactions_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    window.URL?.revokeObjectURL(url);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Profile Views */}
        <Card variant="elevated">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-primary" />
              </div>
              {!isLoadingAnalytics && analyticsData.totalViews > 0 && (
                <span className="text-sm font-semibold text-success flex items-center gap-1">
                  <TrendingUp className="w-[14px] h-[14px]" />
                  {analyticsData?.viewsTrend}
                </span>
              )}
            </div>
            {isLoadingAnalytics ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="text-muted-foreground">{t('Loading...')}</span>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-foreground">{analyticsData?.totalViews?.toLocaleString()}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t('Total Profile Views')}</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* QR Code Scans */}
        <Card variant="elevated">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                <QrCode className="w-6 h-6 text-secondary" />
              </div>
              {!isLoadingAnalytics && analyticsData.qrScans > 0 && (
                <span className="text-sm font-semibold text-success flex items-center gap-1">
                  <TrendingUp className="w-[14px] h-[14px]" />
                  {analyticsData?.scansTrend}
                </span>
              )}
            </div>
            {isLoadingAnalytics ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="text-muted-foreground">{t('Loading...')}</span>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-foreground">{analyticsData?.qrScans?.toLocaleString()}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t('QR Code Scans')}</p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Profile Shares */}
        <Card variant="elevated">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <Share2 className="w-6 h-6 text-success" />
              </div>
              {!isLoadingAnalytics && analyticsData.profileShares > 0 && (
                <span className="text-sm font-semibold text-success flex items-center gap-1">
                  <TrendingUp className="w-[14px] h-[14px]" />
                  {analyticsData?.sharesTrend}
                </span>
              )}
            </div>
            {isLoadingAnalytics ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <span className="text-muted-foreground">{t('Loading...')}</span>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-foreground">{analyticsData?.profileShares?.toLocaleString()}</h3>
                <p className="text-sm text-muted-foreground mt-1">{t('Profile Shares')}</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Contact Details Table */}
      <Card variant="elevated">
        <div className="p-6 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Users className="w-5 h-5" />
                {t('Contact Details Shared')}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {contactDetails?.length} {t('people have shared their contact information')}
              </p>
            </div>
            {contactDetails?.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportAll}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                {t('Export All')}
              </Button>
            )}
          </div>

          {/* Search and Filter */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-[18px] h-[18px] absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder={t('Search by name, email, company, or phone...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full ps-10 pe-4 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="px-4 py-2 border border-input rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              <option value="newest">{t('Newest First')}</option>
              <option value="oldest">{t('Oldest First')}</option>
              <option value="name">{t('Sort by Name')}</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-6 py-3 text-start text-xs font-semibold text-foreground uppercase tracking-wider">
                  {t('Full Name')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-semibold text-foreground uppercase tracking-wider">
                  {t('Email Address')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-semibold text-foreground uppercase tracking-wider">
                  {t('Phone Number')}
                </th>
                <th className="px-6 py-3 text-start text-xs font-semibold text-foreground uppercase tracking-wider">
                  {t('Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {filteredContacts?.length > 0 ? (
                filteredContacts?.map((contact) => (
                  <tr key={contact?.id} className="hover:bg-muted transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                          {contact?.fullName?.charAt(0)}
                        </div>
                        <span className="ms-3 text-sm font-medium text-foreground">{contact?.fullName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <a href={`mailto:${contact?.email}`} className="text-sm text-primary hover:text-primary/80 flex items-center gap-1">
                        <Mail className="w-[14px] h-[14px]" />
                        {contact?.email}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact?.phone ? (
                        <a href={`tel:${contact?.phone}`} className="text-sm text-foreground flex items-center gap-1">
                          <Phone className="w-[14px] h-[14px]" />
                          {contact?.phone}
                        </a>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => handleViewDetails(contact)}
                          className="gap-2"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          {t('View Details')}
                        </Button>
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => handleSaveToPhone(contact)}
                          className="gap-2"
                        >
                          <Download className="w-3.5 h-3.5" />
                          {t('Save to Phone')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Users className="w-12 h-12 text-muted-foreground mb-3" />
                      <p className="text-muted-foreground font-medium">
                        {searchQuery ? t('No contacts found') : t('No contacts yet')}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchQuery
                          ? t('Try adjusting your search criteria')
                          : t('When visitors share their contact info, it will appear here')
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with count */}
        {filteredContacts?.length > 0 && (
          <div className="px-6 py-4 bg-muted border-t border-border">
            <p className="text-sm text-muted-foreground">
              {t('Showing')} {filteredContacts?.length} {t('of')} {contactDetails?.length} {t('contact')}{contactDetails?.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </Card>

      {/* Contact Details Modal — Radix Dialog */}
      <Dialog open={isDetailsModalOpen && !!selectedContact} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
        <DialogContent size="lg">
          {/* Modal Header */}
          <DialogHeader className="flex items-center gap-2">
            <User className="w-6 h-6 text-primary" />
            <DialogTitle>{t('Contact Details')}</DialogTitle>
          </DialogHeader>

          {/* Modal Body */}
          <DialogBody className="space-y-6">
            {selectedContact && (
              <>
                {/* Profile Avatar */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {selectedContact?.fullName?.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold text-foreground">{selectedContact?.fullName}</h4>
                    {selectedContact?.company && (
                      <p className="text-sm text-muted-foreground mt-1">{selectedContact?.company}</p>
                    )}
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  {/* Email */}
                  <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('Email Address')}</p>
                      <a href={`mailto:${selectedContact?.email}`} className="text-sm text-primary hover:text-primary/80 font-medium break-all">
                        {selectedContact?.email}
                      </a>
                    </div>
                  </div>

                  {/* Phone */}
                  {selectedContact?.phone && (
                    <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                      <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-success" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('Phone Number')}</p>
                        <a href={`tel:${selectedContact?.phone}`} className="text-sm text-foreground font-medium">
                          {selectedContact?.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Company */}
                  {selectedContact?.company && (
                    <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                      <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building className="w-5 h-5 text-secondary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('Company')}</p>
                        <p className="text-sm text-foreground font-medium">{selectedContact?.company}</p>
                      </div>
                    </div>
                  )}

                  {/* Date/Time */}
                  <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                    <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-warning" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('Contact Shared On')}</p>
                      <p className="text-sm text-foreground font-medium">{formatDate(selectedContact?.submittedAt)}</p>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('Message')}</p>
                      {selectedContact?.message ? (
                        <p className="text-sm text-foreground whitespace-pre-wrap">{selectedContact?.message}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">{t('No message provided')}</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogBody>

          {/* Modal Footer */}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t('Close')}</Button>
            </DialogClose>
            <Button
              onClick={() => {
                if (selectedContact) {
                  handleSaveToPhone(selectedContact);
                  handleCloseModal();
                }
              }}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              {t('Save to Phone')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InteractionsTab;
